import React, { useState, useRef, useEffect } from 'react';
import type { User } from '@/types/auth';
import { useAuth } from '@/hooks/useAuth';

interface UserMenuProps {
  user: User;
}

export const UserMenu: React.FC<UserMenuProps> = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { logout } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (name?: string) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
          {getInitials(user.name)}
        </div>
        <span className="hidden md:block text-sm font-medium text-gray-900">
          {user.name || 'User'}
        </span>
        <span className="material-symbols-outlined text-gray-600 text-xl">
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900">{user.name || 'User'}</p>
            <p className="text-xs text-gray-600 truncate">{user.email}</p>
          </div>
          
          <button
            onClick={() => {
              setIsOpen(false);
              // Navigate to profile page (TODO: implement)
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-600 text-xl">
              person
            </span>
            Profile
          </button>

          <button
            onClick={() => {
              setIsOpen(false);
              // Navigate to settings page (TODO: implement)
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-600 text-xl">
              settings
            </span>
            Settings
          </button>

          <hr className="my-1 border-gray-200" />

          <button
            onClick={() => {
              setIsOpen(false);
              logout();
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <span className="material-symbols-outlined text-red-600 text-xl">
              logout
            </span>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

